/**
 * Firm Analytics: the periods its pages cover, how a figure is compared with the same period last
 * year, and the time tracker's inputs. A tax firm's year is seasonal, so every comparison is with
 * the same dates a year earlier, never with the previous month.
 */
import { z } from "zod";
import { isRealDate } from "./organizer";

export const ANALYTICS_PAGES = ["overview", "revenue", "billing", "staff", "clients", "growth"] as const;
export type AnalyticsPage = (typeof ANALYTICS_PAGES)[number];
export const ANALYTICS_PAGE_LABELS: Record<AnalyticsPage, string> = {
  overview: "Overview",
  revenue: "Revenue",
  billing: "Billing & Collections",
  staff: "Staff & Capacity",
  clients: "Clients",
  growth: "Growth",
};

export const PERIODS = ["month", "quarter", "ytd", "season", "last12", "year"] as const;
export type PeriodKind = (typeof PERIODS)[number];
export const PERIOD_LABELS: Record<PeriodKind, string> = {
  month: "This month",
  quarter: "This quarter",
  ytd: "Year to date",
  season: "Tax season",
  last12: "Last 12 months",
  year: "Full year",
};

export const analyticsQuery = z
  .object({
    period: z.enum(PERIODS).default("ytd"),
    /** For a full year: which one (defaults to last year). */
    year: z.coerce.number().int().min(2000).max(2100).optional(),
  })
  .strict();
export type AnalyticsQuery = z.infer<typeof analyticsQuery>;

export type DateRange = { from: string; to: string };
export type Period = { kind: PeriodKind; label: string; current: DateRange; previous: DateRange };

const pad = (n: number) => String(n).padStart(2, "0");

/** The same calendar date a year earlier (February 29 becomes February 28). */
export function yearEarlier(day: string): string {
  const y = Number(day.slice(0, 4)) - 1;
  const md = day.slice(5);
  if (md === "02-29") return `${y}-02-28`;
  return `${y}-${md}`;
}

function addDays(day: string, n: number): string {
  const [y, m, d] = day.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/**
 * The dates a period covers as of `today` (the firm's calendar date), and the same dates a year
 * earlier. Periods in progress run to today, so this year to date is compared with last year to
 * the same date.
 */
export function periodRange(kind: PeriodKind, today: string, year?: number): Period {
  const y = Number(today.slice(0, 4));
  const m = Number(today.slice(5, 7));
  let current: DateRange;
  let label = PERIOD_LABELS[kind];
  switch (kind) {
    case "month":
      current = { from: `${y}-${pad(m)}-01`, to: today };
      break;
    case "quarter": {
      const q = Math.floor((m - 1) / 3);
      current = { from: `${y}-${pad(q * 3 + 1)}-01`, to: today };
      label = `Q${q + 1} ${y} to date`;
      break;
    }
    case "ytd":
      current = { from: `${y}-01-01`, to: today };
      break;
    case "season": {
      // January 1 to April 15: this year's season, or the one in progress.
      const end = `${y}-04-15`;
      current = { from: `${y}-01-01`, to: today < end ? today : end };
      label = `${y} tax season`;
      break;
    }
    case "last12":
      current = { from: addDays(yearEarlier(today), 1), to: today };
      break;
    case "year": {
      const which = year && year <= y ? year : y - 1;
      current = { from: `${which}-01-01`, to: which === y ? today : `${which}-12-31` };
      label = which === y ? `${which} to date` : String(which);
      break;
    }
  }
  return { kind, label, current, previous: { from: yearEarlier(current.from), to: yearEarlier(current.to) } };
}

/**
 * How a figure compares with last year. A change worked out from fewer than five items isn't
 * shown as a percentage (one client becoming two is not "+100%"): the two counts are shown instead.
 */
export const SMALL_NUMBER = 5;
export type Change = { kind: "pct"; pct: number } | { kind: "counts" } | { kind: "none" };
export function compareWithLastYear(current: number, previous: number, items: { current: number; previous: number }): Change {
  if (items.current < SMALL_NUMBER || items.previous < SMALL_NUMBER) return items.current || items.previous ? { kind: "counts" } : { kind: "none" };
  if (previous === 0) return { kind: "none" };
  return { kind: "pct", pct: Math.round(((current - previous) / Math.abs(previous)) * 1000) / 10 };
}

// --- Time tracker ----------------------------------------------------------------------------------

export const startTimerSchema = z.object({ engagementId: z.string().uuid(), note: z.string().trim().max(500).optional() }).strict();
export const stopTimerSchema = z.object({ note: z.string().trim().max(500).optional() }).strict();
export const timeEntrySchema = z
  .object({
    engagementId: z.string().uuid(),
    workDate: z.string().refine(isRealDate, "Enter a real date (YYYY-MM-DD)"),
    minutes: z.number().int().min(1, "Enter the time spent").max(1440, "One entry can't be more than 24 hours"),
    note: z.string().trim().max(500).optional(),
  })
  .strict();
export const costRateSchema = z
  .object({
    /** null removes the rate. */
    hourlyCostCents: z.number().int().min(0).max(10_000_000).nullable(),
    weeklyHours: z.number().int().min(1).max(80).default(40),
  })
  .strict();

/** "1h 05m", "45m". */
export function formatMinutes(min: number): string {
  const h = Math.floor(min / 60);
  const m = min % 60;
  return h ? `${h}h ${pad(m)}m` : `${m}m`;
}
